import {
  createSubmitCancelLifecycleContract,
  defineDoctype,
  type DoctypeCatalogMetadata,
  type DoctypeChildCascadePolicy,
  type DoctypeDefinition,
} from "@kubdee/sdk";
import { quotations } from "./schemas/quotations";
import { quotationItems } from "./schemas/quotation-items";
import { salesOrders } from "./schemas/sales-orders";
import { salesOrderItems } from "./schemas/sales-order-items";
import { customers } from "./schemas/customers";
import { customerContacts } from "./schemas/customer-contacts";
import { salesCreditNotes, salesDebitNotes, salesInvoices } from "./schemas/invoices";

// ============================================================================
// Sales module — registered doctypes.
//
// Doctype Admin Epic (#347): B1 (#356, quotation) + #413 (customer) + #422
// (sales_invoice, credit_note).
//
// This file is imported by `modules/sales/index.ts` and re-exported as
// `registeredDoctypes` on the module manifest so the platform boot loop in
// `apps/tenant-worker/src/middleware/auto-migrate.ts` can upsert every
// module's doctype defaults into `core_doctype_registry` on the first
// request per tenant. The boot glue is strictly generic — it reads
// `module.registeredDoctypes` across every enabled module, so adding a new
// doctype only requires appending to `salesDoctypes` below.
//
// Scope:
//   - `quotation` (B1 #356): transactional pilot — `naming`, `custom_fields`,
//     and the rest of the Doctype Admin feature surface.
//   - `customer` (#413): master-data doctype for the selling side. Originally
//     landed under `modules/crm` as the C1 pilot (#359), but the ERPNext /
//     kubdee model treats Customer as a Selling-module entity (Lead /
//     Opportunity stay in CRM; Customer moves once converted). #413 relocates
//     the registration here so the `MODULES.SALES` / `"customer"` pair
//     already used by the live routes matches the doctype's canonical
//     module id.
//   - Invoice/note doctypes (#422/#664/#767): kept below as legacy
//     compatibility metadata only. Accounting owns the active registry entries
//     and accounting_* physical tables; Sales keeps old sales_* schemas/tables
//     for staged migration and historical backfill.
// ============================================================================

export const quotationLifecycle = createSubmitCancelLifecycleContract({
  docStatusField: null,
  submittedStates: ["submitted", "approved", "approve"],
  cancelFromStates: ["draft", "submitted", "approved", "approve"],
  submitDocStatus: null,
  cancelDocStatus: null,
});

export const salesOrderLifecycle = createSubmitCancelLifecycleContract({
  docStatusField: null,
  submittedStates: [
    "submitted",
    "approved",
    "partially_delivered",
    "delivered",
    "partially_billed",
    "invoiced",
  ],
  cancelFromStates: [
    "draft",
    "submitted",
    "approved",
    "partially_delivered",
    "delivered",
    "partially_billed",
  ],
  submitDocStatus: null,
  cancelDocStatus: null,
});

export const salesInvoiceLifecycle = createSubmitCancelLifecycleContract({
  submittedStates: ["submitted", "partially_paid", "paid", "overdue"],
  cancelFromStates: ["draft", "submitted", "partially_paid", "paid", "overdue"],
  reversalPolicy: "reverse_gl",
});

const MASTER_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

const DOCUMENT_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

const CHILD_TABLE_CATALOG = (
  parentDoctype: string,
  parentField: string,
  parentKeyField: string,
  sortField?: string,
  cascadePolicy: DoctypeChildCascadePolicy = "restrict",
): DoctypeCatalogMetadata => ({
  childTable: {
    parentDoctype,
    parentField,
    parentKeyField,
    sortField: sortField ?? null,
    cascadePolicy,
  },
  policy: {
    isExportable: true,
    isReportable: true,
  },
});

/**
 * Quotation (`ใบเสนอราคา`) — the first transactional doctype adopted into
 * the Doctype Admin registry. Pilot for B2 (`<DoctypeForm>` wire-up) and
 * B3 (Playwright smoke) — do not change the id or tableName without
 * coordinating with those issues.
 */
export const quotationDoctype = defineDoctype({
  id: "quotation",
  moduleId: "sales",
  table: quotations,
  label: "ใบเสนอราคา",
  icon: "FileText",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  // #365 — the Naming Series writes its output into sales_quotations.quotation_no
  // on insert. Quotation is a transactional document: its display identity IS
  // the number (e.g. QT-2026-00001), so displayField matches.
  numberingField: "quotation_no",
  displayField: "quotation_no",
  imageField: null,
  // `party_name` is NOT a column on sales_quotations (schema has party_id +
  // party_type only; customer name lives on sales_customers via soft FK).
  // Search across tables would need a JOIN layer that searchFields doesn't
  // support, so we only index what's actually on this row.
  searchFields: ["quotation_no", "status"],
  // #365 follow-up — Thai UI copy for user-facing columns on sales_quotations.
  // Matches labels used in the quotation form/list (see modules/sales/ui/pages/
  // quotations/**/*.tsx). System/audit columns are intentionally omitted.
  columnLabels: {
    quotation_no: "เลขที่ใบเสนอราคา",
    branch_id: "สาขา",
    party_type: "ประเภทลูกค้า",
    party_id: "ลูกค้า",
    quotation_date: "วันที่",
    expiry_date: "วันหมดอายุ",
    subtotal: "ยอดก่อนภาษี",
    tax_amount: "ภาษี",
    total: "ยอดรวม",
    status: "สถานะ",
    remarks: "หมายเหตุ",
    custom_data: "ข้อมูลเพิ่มเติม",
    converted_to_invoice_id: "ใบแจ้งหนี้ที่เชื่อมโยง",
  },
  // Platform-default numbering series — seeded into core_numbering_series
  // on module boot so a fresh tenant can create a quotation without having
  // to configure a series by hand first. Admin can override per-company
  // via the Doctype Admin Naming tab.
  defaultSeries: {
    prefix: "QT-{YYYY}-{#####}",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
  workflowTemplate: {
    name: "ใบเสนอราคา Workflow",
    description: "ร่าง → อนุมัติ → เสร็จ",
    isDefault: true,
    states: [
      { key: "draft", label: "ร่าง", stateType: "start", color: "#94a3b8", docStatus: 0, sortOrder: 0, positionX: 141, positionY: -6 },
      { key: "approve", label: "อนุมัติ", stateType: "intermediate", color: "#3B82F6", docStatus: 1, sortOrder: 1, positionX: 138, positionY: 116 },
      { key: "done", label: "เสร็จ", stateType: "success", color: "#16a34a", docStatus: 1, sortOrder: 2, positionX: 140, positionY: 252 },
    ],
    transitions: [
      { fromStateKey: "draft", toStateKey: "approve", label: "อนุมัติ", sortOrder: 0 },
      { fromStateKey: "approve", toStateKey: "done", label: "เสร็จ", sortOrder: 1 },
    ],
  },
  lifecycle: quotationLifecycle,
});

/**
 * Customer (`ลูกค้า`) — the master-data pilot for the Doctype Admin
 * registry. Relocated here from `modules/crm/doctypes.ts` under #413: the
 * customer concept belongs to the Selling module (ERPNext parity — Lead /
 * Opportunity stay in CRM; once converted, Customer moves), and the live
 * create/activity paths (`modules/sales/routes/customers.ts`) already key
 * numbering + activity by `MODULES.SALES` — pairing the doctype's `moduleId`
 * matches.
 *
 * Issue #662 split the sales customer doctype onto its own physical
 * `sales_customers` table. The table keeps the historical partner-shaped
 * column names (`partner_code`, `partner_name`, etc.) so existing API
 * consumers do not need a wire-shape migration in the same release.
 *
 * `defaultSeries` seeds `core_numbering_series` with
 * `(module=sales, doctype=customer, company_id=NULL)` on tenant boot,
 * matching the pair at `customers.ts:186` so `generateNextNumber()`
 * resolves the seed instead of throwing NOT_FOUND.
 *
 * Do not change `id` or `tableName` without coordinating with C2 (#360)
 * which wires `<CustomerForm>` against this exact contract.
 */
export const customerDoctype = defineDoctype({
  id: "customer",
  moduleId: "sales",
  table: customers,
  label: "ลูกค้า",
  icon: "Users",
  features: [
    "naming",
    "custom_fields",
    "workflow",
    "print",
    "permissions",
    "automation",
    "list_view",
  ],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: "partner_code",
  displayField: "partner_name",
  imageField: null,
  searchFields: ["partner_name", "partner_code"],
  columnLabels: {
    partner_code: "รหัสลูกค้า",
    partner_name: "ชื่อลูกค้า",
    custom_data: "ข้อมูลเพิ่มเติม",
  },
  defaultSeries: {
    prefix: "C-#####",
    resetEvery: "never",
    seriesName: "Standard",
  },
});

export const customerContactDoctype = defineDoctype({
  id: "customer_contact",
  moduleId: "sales",
  table: customerContacts,
  label: "ช่องทางติดต่อลูกค้า",
  icon: "Contact",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "child_table",
  catalog: CHILD_TABLE_CATALOG("customer", "contacts", "customer_id"),
  numberingField: null,
  displayField: "contact_value",
  imageField: null,
  searchFields: ["contact_value"],
  columnLabels: {
    customer_id: "ลูกค้า",
    channel_id: "ช่องทาง",
    contact_value: "ข้อมูลติดต่อ",
    is_primary: "ช่องทางหลัก",
    notes: "หมายเหตุ",
  },
});

/**
 * Sales Order (`ใบสั่งขาย`) — transactional doctype owned by sales.
 *
 * Stored in `sales_orders` with its own `sales_order_items` child table.
 * Fulfillment remains inventory-owned (delivery_order) and billing is
 * Accounting-owned (sales_invoice), so this doctype is the sales-side
 * commercial commitment only.
 */
export const salesOrderDoctype = defineDoctype({
  id: "sales_order",
  moduleId: "sales",
  table: salesOrders,
  label: "ใบสั่งขาย",
  icon: "FileText",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "order_no",
  displayField: "order_no",
  imageField: null,
  searchFields: ["order_no", "status"],
  columnLabels: {
    order_no: "เลขที่ใบสั่งขาย",
    branch_id: "สาขา",
    party_type: "ประเภทคู่ค้า",
    party_id: "ลูกค้า",
    order_date: "วันที่",
    expiry_date: "วันหมดอายุ",
    subtotal: "ยอดก่อนภาษี",
    tax_amount: "ภาษี",
    total: "ยอดรวม",
    status: "สถานะ",
    source_quotation_id: "ใบเสนอราคาต้นทาง",
    converted_to_invoice_id: "ใบแจ้งหนี้ที่เชื่อมโยง",
    remarks: "หมายเหตุ",
    custom_data: "ข้อมูลเพิ่มเติม",
  },
  defaultSeries: {
    prefix: "SO-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
  lifecycle: salesOrderLifecycle,
});

/**
 * Sales Invoice (`ใบแจ้งหนี้ขาย`) — legacy compatibility metadata.
 *
 * Issue #767 moves active ownership to Accounting while preserving 1 doctype
 * = 1 physical table there. The old source table is archived under
 * `sales_legacy_invoices_archive`. Keep this definition exported for old
 * tests, migrations, and staged backfill checks, but do not include it in
 * `salesDoctypes`.
 */
export const salesInvoiceDoctype = defineDoctype({
  id: "sales_invoice",
  moduleId: "sales",
  table: salesInvoices,
  label: "ใบแจ้งหนี้ขาย",
  icon: "FileText",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "invoice_no",
  displayField: "invoice_no",
  imageField: null,
  searchFields: ["invoice_no", "status"],
  columnLabels: {
    invoice_no: "เลขที่ใบแจ้งหนี้",
    branch_id: "สาขา",
    party_type: "ประเภทคู่ค้า",
    party_id: "ลูกค้า",
    invoice_date: "วันที่",
    due_date: "วันครบกำหนด",
    subtotal: "ยอดก่อนภาษี",
    tax_amount: "ภาษี",
    total: "ยอดรวม",
    outstanding: "ค้างชำระ",
    status: "สถานะ",
    remarks: "หมายเหตุ",
  },
  defaultSeries: {
    prefix: "SINV-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
  lifecycle: salesInvoiceLifecycle,
});

/**
 * Credit Note (`ใบลดหนี้`) — legacy compatibility metadata (ม.86/10).
 *
 * Accounting owns the active `credit_note` registry/table after #767. The old
 * sales source table is archived as `sales_legacy_credit_notes_archive`.
 */
export const creditNoteDoctype = defineDoctype({
  id: "credit_note",
  moduleId: "sales",
  table: salesCreditNotes,
  label: "ใบลดหนี้",
  icon: "FileMinus",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "invoice_no",
  displayField: "invoice_no",
  imageField: null,
  searchFields: ["invoice_no", "status"],
  columnLabels: {
    invoice_no: "เลขที่ใบลดหนี้",
    branch_id: "สาขา",
    party_id: "ลูกค้า",
    invoice_date: "วันที่",
    credit_note_of: "ใบแจ้งหนี้อ้างอิง",
    reason_code: "เหตุผล",
    original_invoice_amount: "ยอดเดิม",
    corrected_amount: "ยอดหลังปรับ",
    difference_amount: "ยอดต่าง",
    total: "ยอดรวม",
    status: "สถานะ",
    remarks: "หมายเหตุ",
  },
  defaultSeries: {
    prefix: "CN-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
  lifecycle: salesInvoiceLifecycle,
});

/**
 * Sales Debit Note (`ใบเพิ่มหนี้ขาย`) — legacy compatibility metadata.
 *
 * Accounting owns the active customer-side DN doctype/table after #767. The
 * old sales source table is archived as `sales_legacy_debit_notes_archive`.
 */
export const salesDebitNoteDoctype = defineDoctype({
  id: "sales_debit_note",
  moduleId: "sales",
  table: salesDebitNotes,
  label: "ใบเพิ่มหนี้",
  icon: "FilePlus",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "invoice_no",
  displayField: "invoice_no",
  imageField: null,
  searchFields: ["invoice_no", "status"],
  columnLabels: {
    invoice_no: "เลขที่ใบเพิ่มหนี้",
    branch_id: "สาขา",
    party_id: "ลูกค้า",
    invoice_date: "วันที่",
    debit_note_of: "ใบแจ้งหนี้อ้างอิง",
    reason_code: "เหตุผล",
    original_invoice_amount: "ยอดเดิม",
    corrected_amount: "ยอดหลังปรับ",
    difference_amount: "ยอดต่าง",
    total: "ยอดรวม",
    status: "สถานะ",
    remarks: "หมายเหตุ",
  },
  defaultSeries: {
    prefix: "DN-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
  lifecycle: salesInvoiceLifecycle,
});

export const quotationItemDoctype = defineDoctype({
  id: "quotation_item",
  moduleId: "sales",
  table: quotationItems,
  label: "รายการในใบเสนอราคา",
  icon: "Rows3",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "child_table",
  catalog: CHILD_TABLE_CATALOG("quotation", "items", "quotation_id", "sort_order"),
  numberingField: null,
  displayField: "description",
  imageField: null,
  searchFields: ["item_id", "description"],
  columnLabels: {
    quotation_id: "ใบเสนอราคา",
    item_id: "สินค้า",
    description: "รายละเอียด",
    qty: "จำนวน",
    rate: "ราคา/หน่วย",
    amount: "มูลค่ารวม",
    account_id: "บัญชี",
    tax_template_id: "รูปแบบภาษี",
    tax_amount: "ภาษี",
    sort_order: "ลำดับ",
  },
});

export const salesOrderItemDoctype = defineDoctype({
  id: "sales_order_item",
  moduleId: "sales",
  table: salesOrderItems,
  label: "รายการในใบสั่งขาย",
  icon: "Rows3",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "child_table",
  catalog: CHILD_TABLE_CATALOG("sales_order", "items", "sales_order_id", "sort_order"),
  numberingField: null,
  displayField: "description",
  imageField: null,
  searchFields: ["item_id", "description"],
  columnLabels: {
    sales_order_id: "ใบสั่งขาย",
    item_id: "สินค้า",
    description: "รายละเอียด",
    qty: "จำนวน",
    rate: "ราคา/หน่วย",
    amount: "มูลค่ารวม",
    delivered_qty: "จำนวนที่ส่งแล้ว",
    delivered_amount: "มูลค่าที่ส่งแล้ว",
    billed_qty: "จำนวนที่วางบิลแล้ว",
    billed_amount: "มูลค่าที่วางบิลแล้ว",
    account_id: "บัญชี",
    tax_template_id: "รูปแบบภาษี",
    tax_amount: "ภาษี",
    sort_order: "ลำดับ",
  },
});

export const legacyAccountingDocumentDoctypes: readonly DoctypeDefinition[] = Object.freeze([
  salesInvoiceDoctype,
  creditNoteDoctype,
  salesDebitNoteDoctype,
]);

/**
 * Every doctype the sales module wants registered with the platform. The
 * order here is preserved in the Admin UI through `listDoctypes()` sort.
 */
export const salesDoctypes: readonly DoctypeDefinition[] = Object.freeze([
  customerDoctype,
  customerContactDoctype,
  quotationDoctype,
  quotationItemDoctype,
  salesOrderDoctype,
  salesOrderItemDoctype,
]);
